import {
  DOCUMENT_PANE_MIN_WIDTH,
  EDITOR_PANE_MIN_WIDTH,
  MAX_DOCUMENT_PANES,
} from 'constants/loanApplication';
import { SCREEN_WIDTH } from 'constants/ui';

const FULL_PERCENT = 100;
const SINGLE_PANE_EDITOR_PERCENT = 60;

// Throttle for the pane offer/capacity resize listeners. Named distinctly from
// useContentWidth's RESIZE_THROTTLE_MS (500) to avoid a same-name-different-value clash.
export const PANE_RESIZE_THROTTLE_MS = 200;

/**
 * Whether to OFFER document side-panes: a genuine multi-monitor setup or a single
 * ultrawide viewport. Pure so it can be unit-tested; the hook reads the live globals.
 *
 * @param {{ isScreenExtended: boolean, innerWidth: number }} params
 * @returns {boolean}
 */
export const getIsWideWorkspace = ({ isScreenExtended, innerWidth }) =>
  !!isScreenExtended || innerWidth >= SCREEN_WIDTH.ULTRAWIDE;

/**
 * How many document panes fit beside the editor at the current window width, clamped to
 * the hard cap. Governs whether "add another pane" is offered (capacity ≠ the offer gate).
 *
 * @param {number} width Live window width
 * @returns {number}
 */
export const getMaxDocumentPanes = width => {
  const fittable = Math.floor((width - EDITOR_PANE_MIN_WIDTH) / DOCUMENT_PANE_MIN_WIDTH);

  return Math.max(0, Math.min(MAX_DOCUMENT_PANES, fittable));
};

/**
 * react-split sizes (percent) for the OUTER editor-vs-document-group split. react-split applies the
 * `sizes` percentages on initial render and only enforces `minSize` while dragging — so the group
 * share is derived from the live content width to guarantee the group gets at least
 * `paneCount × DOCUMENT_PANE_MIN_WIDTH` pixels (clamped so the editor keeps its own minimum).
 * Falls back to static shares before the host width is measured.
 *
 * @param {number} paneCount Number of open document panes (1 or 2)
 * @param {number} [contentWidth] Measured width of the split host
 * @returns {[number, number]} [editor, documentGroup]
 */
export const getOuterSplitSizes = (paneCount, contentWidth) => {
  if (!contentWidth) {
    return paneCount === 1
      ? [SINGLE_PANE_EDITOR_PERCENT, FULL_PERCENT - SINGLE_PANE_EDITOR_PERCENT]
      : [FULL_PERCENT / 2, FULL_PERCENT / 2];
  }

  const groupWidth = Math.min(
    paneCount * DOCUMENT_PANE_MIN_WIDTH,
    contentWidth - EDITOR_PANE_MIN_WIDTH,
  );
  const groupPercent = (groupWidth / contentWidth) * FULL_PERCENT;

  return [FULL_PERCENT - groupPercent, groupPercent];
};

/**
 * Minimum pixel widths for the OUTER editor-vs-document-group split. The document group's
 * floor scales with how many panes it holds so two panes can't be squeezed below usable width.
 *
 * @param {number} paneCount Number of open document panes
 * @returns {[number, number]} [editorMin, documentGroupMin]
 */
export const getOuterMinSizes = paneCount => [
  EDITOR_PANE_MIN_WIDTH,
  paneCount * DOCUMENT_PANE_MIN_WIDTH,
];

/**
 * Default react-split sizes (percent) for the INNER document-pane split: an even split between
 * two document panes, or a single full-width pane.
 *
 * @param {number} paneCount Number of open document panes (1 or 2)
 * @returns {number[]}
 */
export const getInnerPaneSizes = paneCount =>
  paneCount === MAX_DOCUMENT_PANES ? [FULL_PERCENT / 2, FULL_PERCENT / 2] : [FULL_PERCENT];

/**
 * The document-fetch params bag shared by the Documents tab and the editor's document panes.
 * Kept in one place so a future field (e.g. an include flag) can't drift between call sites.
 *
 * @param {{ loanId: string, applicationId: string }} params
 */
export const buildDocumentLoanParams = ({ loanId, applicationId }) => ({
  loanIdOrLegacyLoanNumber: loanId,
  applicationId,
  includeDeleted: true,
  includeFieldsAndResults: false,
});
